import { type FileMessage } from '@sendbird/chat/message';
import FileViewer from '@sendbird/uikit-react/ui/FileViewer';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
    type DataTableRowEditEvent,
} from 'primereact/datatable';
import { useEffect, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    DownloadIcon,
    FileIcon,
    Icon,
    IconButton,
    type IconType,
    ImageIcon,
    Spinner,
    useToastController,
    VideoIcon,
    XStack,
    YStack,
} from 'smoothie';

import { TextEditor } from '../../common/components/GridCellEditors';
import { ViewItemRenderer } from '../../common/components/GridCellRenderers';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import type { MembershipDetails } from '../../entities/entityTypes';
import { TrackedEvent } from '../../logger/Events';
import { captureException, trackEvent } from '../../logger/Logger';
import { useGetChannelFileMessages } from '../hooks/useGetChannelFileMessages';
import { useGetSendbirdChannel } from '../hooks/useGetSendbirdChannel';
import { useUpdateDocumentVaultDisplayName } from '../hooks/useUpdateDocumentVaultDisplayName';
import {
    getDocumentVaultDisplayNames,
    getDocumentVaultFileDisplayName,
    getOriginalFileName,
    shouldPersistDocumentVaultDisplayName,
} from '../utils/documentVaultChannelData';

interface DocumentVaultProps {
    membership: MembershipDetails;
}

interface DocumentVaultRow {
    messageId: number;
    displayFileName: string;
    originalFileName: string;
    fileSize: number;
    fileType: string | undefined;
    fileUrl: string;
    createdAt: number;
    senderName: string | null;
    fileMessage: FileMessage;
}

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round(bytes / k ** i)} ${sizes[i]}`;
};

const isViewableImageType = (type: string | undefined): boolean =>
    type != null && type !== '' && type.startsWith('image/');

const getFileIcon = (type: string | undefined): IconType => {
    if (type == null || type === '') return FileIcon;
    if (type.startsWith('image/')) return ImageIcon;
    if (type.startsWith('video/')) return VideoIcon;
    return FileIcon;
};

const toDocumentVaultRows = (
    fileMessages: FileMessage[],
    displayNames: Record<string, string>,
): DocumentVaultRow[] =>
    fileMessages.map((fileMessage) => ({
        messageId: fileMessage.messageId,
        displayFileName: getDocumentVaultFileDisplayName(
            fileMessage.messageId,
            fileMessage,
            displayNames,
        ),
        originalFileName: getOriginalFileName(fileMessage),
        fileSize: fileMessage.size || 0,
        fileType: fileMessage.type,
        fileUrl: fileMessage.url,
        createdAt: fileMessage.createdAt,
        senderName:
            fileMessage.sender.nickname || fileMessage.sender.userId || null,
        fileMessage,
    }));

const downloadFile = (fileUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const getDocumentVaultFileEventData = (
    membershipId: string,
    row: DocumentVaultRow,
) => ({
    membershipId,
    messageId: row.messageId,
    fileName: row.displayFileName,
    originalFileName: row.originalFileName,
    fileSize: row.fileSize,
    fileType: row.fileType ?? '',
});

const downloadRenderer = (
    row: DocumentVaultRow,
    onDownload: (row: DocumentVaultRow) => void,
) => (
    <YStack justifyContent="center" alignItems="center">
        <IconButton
            variant="tertiary"
            icon={DownloadIcon}
            onPress={() => {
                onDownload(row);
            }}
        />
    </YStack>
);

const viewRenderer = (
    row: DocumentVaultRow,
    onView: (row: DocumentVaultRow) => void,
) => (
    <ViewItemRenderer
        onPress={() => {
            onView(row);
        }}
    />
);

const displayNameRenderer = (row: DocumentVaultRow) => (
    <XStack gap={8} alignItems="center" width="100%">
        <Icon icon={getFileIcon(row.fileType)} size={20} color="$gray1000" />
        <BodySmallRegular>{row.displayFileName}</BodySmallRegular>
    </XStack>
);

export const DocumentVault = ({ membership }: DocumentVaultProps) => {
    const toast = useToastController();
    const { channel, isLoading: isLoadingChannel } =
        useGetSendbirdChannel(membership);
    const {
        fileMessages,
        isLoading: isLoadingMessages,
        hasLoaded: hasLoadedFileMessages,
        error: fileMessagesError,
    } = useGetChannelFileMessages(channel);
    const { updateDisplayName } = useUpdateDocumentVaultDisplayName(channel);
    const [selectedMessage, setSelectedMessage] = useState<FileMessage | null>(
        null,
    );
    const [displayNames, setDisplayNames] = useState<Record<string, string>>(
        {},
    );
    const [rows, setRows] = useState<DocumentVaultRow[]>([]);
    const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (channel == null) {
            setDisplayNames({});
            return;
        }

        setDisplayNames(getDocumentVaultDisplayNames(channel.data));
    }, [channel]);

    useEffect(() => {
        setRows(toDocumentVaultRows(fileMessages, displayNames));
    }, [displayNames, fileMessages]);

    const hasMessagingChannelUrl =
        membership.messagingChannel?.channelUrl != null;
    const isLoadingFiles =
        channel != null && (!hasLoadedFileMessages || isLoadingMessages);
    const isLoading = isLoadingChannel || isLoadingFiles;
    const channelNotFound =
        !isLoadingChannel && (!hasMessagingChannelUrl || channel == null);
    const canEditDisplayName = channel != null;

    const onRowEditChange = (event: DataTableRowEditEvent) => {
        setEditingRows(event.data);
    };

    const onRowEditComplete = async ({
        newData,
        index,
    }: DataTableRowEditCompleteEvent) => {
        const newRow = newData as DocumentVaultRow;
        const originalRow = rows[index];

        if (originalRow == null) {
            return;
        }

        const nextDisplayName = newRow.displayFileName;

        if (
            !shouldPersistDocumentVaultDisplayName(
                originalRow.messageId,
                originalRow.fileMessage,
                displayNames,
                nextDisplayName,
            )
        ) {
            setRows(toDocumentVaultRows(fileMessages, displayNames));
            return;
        }

        const previousDisplayName = getDocumentVaultFileDisplayName(
            originalRow.messageId,
            originalRow.fileMessage,
            displayNames,
        );

        try {
            const updatedChannel = await updateDisplayName(
                originalRow.messageId,
                nextDisplayName,
            );
            const nextDisplayNames = getDocumentVaultDisplayNames(
                updatedChannel.data,
            );
            setDisplayNames(nextDisplayNames);
            trackEvent(TrackedEvent.DocumentVaultFileRenamed, {
                membershipId: membership.id,
                messageId: originalRow.messageId,
                previousDisplayName,
                nextDisplayName: getDocumentVaultFileDisplayName(
                    originalRow.messageId,
                    originalRow.fileMessage,
                    nextDisplayNames,
                ),
                originalFileName: originalRow.originalFileName,
            });
            toast.show('File name updated', MutationSuccessToastOptions);
        } catch (error) {
            captureException(error);
            setRows(toDocumentVaultRows(fileMessages, displayNames));
            toast.show(
                'Could not update file name',
                MutationErrorToastOptions(error),
            );
        }
    };

    const handleDownloadFile = (row: DocumentVaultRow) => {
        trackEvent(
            TrackedEvent.DocumentVaultFileDownloaded,
            getDocumentVaultFileEventData(membership.id, row),
        );
        downloadFile(row.fileUrl, row.displayFileName);
    };

    const handleViewFile = (row: DocumentVaultRow) => {
        if (!isViewableImageType(row.fileType)) {
            handleDownloadFile(row);
            return;
        }

        trackEvent(
            TrackedEvent.DocumentVaultFileViewed,
            getDocumentVaultFileEventData(membership.id, row),
        );
        setSelectedMessage(row.fileMessage);
    };

    return (
        <>
            <YStack gap={12} width="100%">
                {isLoading ? (
                    <YStack
                        alignItems="center"
                        justifyContent="center"
                        paddingVertical={40}
                    >
                        <Spinner />
                    </YStack>
                ) : channelNotFound ? (
                    <BodyMediumRegular fontStyle="italic">
                        This membership&apos;s messaging channel could not be
                        found
                    </BodyMediumRegular>
                ) : fileMessagesError != null ? (
                    <BodyMediumRegular fontStyle="italic">
                        Documents could not be loaded. Please try again later.
                    </BodyMediumRegular>
                ) : (
                    <DataTable
                        value={rows}
                        dataKey="messageId"
                        size="small"
                        tableStyle={{ width: '100%' }}
                        sortField="createdAt"
                        sortOrder={-1}
                        scrollable
                        scrollHeight="70vh"
                        emptyMessage="No documents yet. Files sent in messages will appear here."
                        editMode="row"
                        editingRows={editingRows}
                        onRowEditChange={onRowEditChange}
                        onRowEditComplete={(event) => {
                            void onRowEditComplete(event);
                        }}
                    >
                        <Column
                            field="displayFileName"
                            header="Display Name"
                            body={displayNameRenderer}
                            editor={(options) => (
                                <TextEditor options={options} />
                            )}
                            sortable
                            style={{ minWidth: 320 }}
                        />
                        <Column
                            field="originalFileName"
                            header="Original Name"
                            body={({ originalFileName }: DocumentVaultRow) => (
                                <BodySmallRegular>
                                    {originalFileName}
                                </BodySmallRegular>
                            )}
                            sortable
                            style={{ minWidth: 220 }}
                        />
                        <Column
                            field="fileSize"
                            header="Size"
                            body={({ fileSize }: DocumentVaultRow) => (
                                <BodySmallRegular>
                                    {formatFileSize(fileSize)}
                                </BodySmallRegular>
                            )}
                            sortable
                            style={{ minWidth: 100 }}
                        />
                        <Column
                            field="senderName"
                            header="Sender"
                            body={({ senderName }: DocumentVaultRow) => (
                                <BodySmallRegular>
                                    {senderName ?? '-'}
                                </BodySmallRegular>
                            )}
                            sortable
                            style={{ minWidth: 160 }}
                        />
                        <Column
                            field="createdAt"
                            header="Sent At"
                            body={({ createdAt }: DocumentVaultRow) =>
                                formatToSimpleDateTimeString(
                                    new Date(createdAt),
                                )
                            }
                            sortable
                            style={{ minWidth: 180 }}
                        />
                        <Column
                            header="View"
                            body={(row: DocumentVaultRow) =>
                                isViewableImageType(row.fileType)
                                    ? viewRenderer(row, handleViewFile)
                                    : null
                            }
                            style={{ minWidth: 60, width: 60 }}
                        />
                        <Column
                            header="Download"
                            body={(row: DocumentVaultRow) =>
                                downloadRenderer(row, handleDownloadFile)
                            }
                            style={{ minWidth: 80, width: 80 }}
                        />
                        <Column
                            rowEditor
                            header="Edit"
                            hidden={!canEditDisplayName}
                            style={{ minWidth: 80, width: 80 }}
                        />
                    </DataTable>
                )}
            </YStack>
            {selectedMessage != null && (
                <FileViewer
                    message={selectedMessage}
                    onClose={() => {
                        setSelectedMessage(null);
                    }}
                    isByMe={false}
                />
            )}
        </>
    );
};
